'use client';

import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  lazy,
  Suspense,
} from 'react';
import { Box, Spinner } from '@chakra-ui/react';
import type {
  Broker,
  NestedAutocompleteAddress,
  NestedHome,
  SellerIntentType,
} from '@habity/types';
import type { SellStepperRef } from '~/components/onboarding/sell/SellStepper';
import { fetchAndEnrichAddress } from '~/lib/sell/addressSelection';
import { enrichAddressWithRegistry } from '~/lib/realai/residential-registry';
import { getAPI } from '~/lib/api';
import { API_PATHS } from '@habity/constants';
import { getFiltersFromHome, getSearchParams } from '@habity/services';

// Lazy load the stepper
const SellStepper = lazy(() =>
  import('~/components/onboarding/sell/SellStepper').then((mod) => ({
    default: mod.SellStepper,
  }))
);

export interface SellStepperContainerProps {
  initialAddress?: NestedAutocompleteAddress | null;
  initialBroker?: Broker | null;
  addressBroker?: Broker | null;
  intent: SellerIntentType;
  locationName?: string | null;
  leadName?: string;
  leadEmail?: string;
  leadPhone?: string;
  leadSourceId?: string;
  assignmentSourceId?: string;
  onComplete?: (home?: NestedHome) => void;
  onStepChange?: (step: number, total: number) => void;
  children?: (props: {
    stepperRef: React.RefObject<SellStepperRef | null>;
    stepperElement: React.ReactNode;
    home: Partial<NestedHome> | null;
    selectedAddress: NestedAutocompleteAddress | null;
    broker: Broker | null;
    valuation: number | null;
    currentStep?: number;
    totalSteps?: number;
  }) => React.ReactNode;
}

/**
 * Shared container for SellStepper logic
 * Used by both /sell page and SellStepperModal
 */
export function SellStepperContainer({
  initialAddress,
  initialBroker,
  addressBroker,
  intent,
  locationName,
  leadName,
  leadEmail,
  leadPhone,
  leadSourceId,
  assignmentSourceId,
  onComplete,
  onStepChange,
  children,
}: SellStepperContainerProps) {
  const stepperRef = useRef<SellStepperRef>(null);
  const [home, setHome] = useState<Partial<NestedHome> | null>(null);
  const [selectedAddress, setSelectedAddress] =
    useState<NestedAutocompleteAddress | null>(initialAddress || null);
  const [broker, setBroker] = useState<Broker | null>(
    initialBroker || addressBroker || null
  );
  const [valuation, setValuation] = useState<number | null>(null);
  const [ownerId, setOwnerId] = useState<number | null>(null);
  const [isLoadingBroker, setIsLoadingBroker] = useState(false);
  const [currentStep, setCurrentStep] = useState<number>(0);
  const [totalSteps, setTotalSteps] = useState<number>(4);

  // Enrich address with registry data when address changes
  useEffect(() => {
    if (selectedAddress && !selectedAddress.living_space) {
      enrichAddressWithRegistry(selectedAddress).then((enrichedAddress) => {
        setSelectedAddress(enrichedAddress);
      });
    }
  }, [selectedAddress?.id]);

  // Fetch broker when address changes (if no broker is set)
  useEffect(() => {
    if (selectedAddress && !broker && !isLoadingBroker) {
      setIsLoadingBroker(true);
      const homeData = { address: selectedAddress } as unknown as NestedHome;
      const filters = getFiltersFromHome(homeData);
      const brokerSearchParams = getSearchParams(
        filters,
        selectedAddress.id ? selectedAddress.id.toString() : undefined
      );
      getAPI(
        API_PATHS.PROVIDERS.NEONDATABASE.BROKERS,
        brokerSearchParams?.toString() + `&limit=1`
      )
        .then((brokers) => {
          if (brokers && brokers.length > 0) {
            setBroker(brokers[0]);
          }
        })
        .catch((error) => {
          console.error('Error fetching broker:', error);
        })
        .finally(() => {
          setIsLoadingBroker(false);
        });
    }
  }, [selectedAddress?.id, broker, isLoadingBroker]);

  const resetClaimState = useCallback(() => {
    setHome(null);
  }, []);

  const handleAddressSelection = useCallback(
    async (
      addressId?: number,
      originalStreetName?: string,
      customAddress?: NestedAutocompleteAddress
    ) => {
      const updatedAddress = await fetchAndEnrichAddress(
        addressId,
        originalStreetName,
        customAddress
      );

      if (!updatedAddress) {
        return;
      }

      setSelectedAddress(updatedAddress);

      // Create home data from address (same as page flow)
      const homeData = { address: updatedAddress } as Partial<NestedHome>;
      setHome(homeData);
    },
    []
  );

  const stepperElement = (
    <Suspense
      fallback={
        <Box p={4}>
          <Spinner />
        </Box>
      }
    >
      <SellStepper
        ref={stepperRef}
        home={home}
        setHome={setHome}
        selectedAddress={selectedAddress}
        setSelectedAddress={setSelectedAddress}
        brokerId={broker?.id}
        selectedBroker={broker}
        resetClaimState={resetClaimState}
        onAddressSelected={handleAddressSelection}
        onComplete={onComplete}
        intent={intent}
        leadName={leadName}
        leadEmail={leadEmail}
        leadPhone={leadPhone}
        leadSourceId={leadSourceId}
        assignmentSourceId={assignmentSourceId}
        valuation={valuation}
        setValuation={setValuation}
        ownerId={ownerId}
        setOwnerId={setOwnerId}
        onStepChange={onStepChange}
      />
    </Suspense>
  );

  useEffect(() => {
    if (stepperRef.current) {
      setTotalSteps(stepperRef.current.getTotalSteps());
    }
  }, []);

  // If children render prop is provided, use it
  if (children) {
    return (
      <>
        {children({
          stepperRef,
          stepperElement,
          home,
          selectedAddress,
          broker,
          valuation,
          currentStep,
          totalSteps,
        })}
      </>
    );
  }

  // Otherwise, just render the stepper
  return stepperElement;
}
